import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Search, Flag, Scale, ArrowRight, Quote } from "lucide-react";
import { StarRating, VerifiedBadge } from "@/components/shared";

const HERO = "https://images.unsplash.com/photo-1522071820081-009f0129c71c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODh8MHwxfHNlYXJjaHwzfHxvZmZpY2UlMjB0ZWNoJTIwd29ya2Vyc3xlbnwwfHx8fDE3ODIzMTM4ODl8MA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: ShieldCheck, title: "Verified Employees", desc: "Optional private work-email verification grants a Verified badge, so you can trust the source without exposing identity." },
  { icon: Search, title: "Transparent Moderation", desc: "Every review shows its lifecycle: Under Review → Published or Declined with a reason." },
  { icon: Flag, title: "Red-Flag Signals", desc: "Payout reliability, licensing status and compliance concerns surfaced clearly." },
  { icon: Scale, title: "Fair Disputes", desc: "Structured takedown and legal-request workflow for employers, never buried in policy." },
];

export default function Landing() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [featured, setFeatured] = useState([]);
  const [wall, setWall] = useState([]);

  useEffect(() => {
    api.get("/companies", { params: { sort: "reviews" } }).then(({ data }) => setFeatured(data.slice(0, 3))).catch(() => {});
    api.get("/reviews/recent", { params: { verified: true, limit: 9 } })
      .then(({ data }) => setWall(data))
      .catch(() => {});
  }, []);

  return (
    <main>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-[#DDDAD1]">
        <div className="mx-auto grid grid-cols-1 max-w-[1800px] gap-8 px-6 py-10 sm:px-10 sm:py-16 lg:grid-cols-2 lg:gap-12 lg:px-16 lg:py-28">
          <div className="animate-fade-up">
            <p className="eyebrow mb-5">Written from experience</p>
            <h1 className="text-4xl font-extrabold leading-[1.05] sm:text-5xl lg:text-6xl">
              The iGaming industry, <span className="text-teal-400">reviewed by the people who work in it.</span>
            </h1>
            <p className="mt-6 max-w-3xl text-lg leading-relaxed text-slate-300">
              Honest, anonymous accounts of what it is really like to work for the industry's operators,
              suppliers and studios. Written from experience, not from press releases.
            </p>

            <form
              onSubmit={(e) => { e.preventDefault(); nav(`/companies?q=${encodeURIComponent(q)}`); }}
              className="mt-8 flex items-center gap-2 rounded-full border border-[#DDDAD1] bg-ink-900 p-1.5"
            >
              <Search className="ml-3 h-5 w-5 text-slate-400" aria-hidden="true" />
              <input
                data-testid="hero-search"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search employers, roles, or keywords…"
                className="min-w-0 flex-1 bg-transparent px-2 py-2.5 text-base outline-none placeholder:text-slate-500"
              />
              <button data-testid="hero-search-btn" className="btn-teal rounded-full text-sm">Search</button>
            </form>

            <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500">
              Every reviewer is verified as a current or former industry employee. Nothing else is edited.
            </p>

            <dl className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
              {[["1M+", "Reviews"], ["20K+", "Companies"], ["100%", "Verified"], ["4.2/5", "Avg Rating"]].map(([n, l]) => (
                <div key={l}>
                  <dt className="font-heading text-2xl font-semibold text-ink sm:text-3xl">{n}</dt>
                  <dd className="mt-1 text-sm text-slate-500">{l}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <img src={HERO} alt="Employees collaborating in an office" className="relative h-full max-h-[460px] w-full rounded-3xl border border-[#DDDAD1] object-cover" />
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="mx-auto max-w-[1800px] px-6 py-12 sm:px-10 sm:py-20 lg:px-16">
        <p className="eyebrow mb-3">Why iGamingTruth</p>
        <h2 className="max-w-3xl text-3xl font-bold">Credibility engineered into every review.</h2>
        <div className="mt-8 grid gap-6 sm:mt-12 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4">
          {features.map((f, i) => (
            <div key={f.title} className="card-surface lift p-6 animate-fade-up" style={{ animationDelay: `${i * 80}ms` }}>
              <span className="grid h-11 w-11 place-items-center rounded-xl bg-teal-500/15 text-teal-400">
                <f.icon className="h-6 w-6" aria-hidden="true" />
              </span>
              <h3 className="mt-5 text-lg font-semibold">{f.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured companies */}
      {featured.length > 0 && (
        <section className="mx-auto max-w-[1800px] px-6 pb-14 sm:px-10 sm:pb-24 lg:px-16">
          <div className="flex items-end justify-between">
            <div>
              <p className="eyebrow mb-3">Most reviewed</p>
              <h2 className="text-3xl font-bold">Companies people are talking about</h2>
            </div>
            <Link to="/companies" className="hidden items-center gap-1 text-sm text-teal-400 hover:text-teal-300 sm:flex">
              View all <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="mt-8 grid gap-6 sm:mt-10 md:grid-cols-3">
            {featured.map((c) => (
              <Link key={c.id} to={`/company/${c.id}`} data-testid="featured-company" className="card-surface lift p-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{c.name}</h3>
                  <span className="font-heading text-2xl font-bold text-teal-400">{c.avg_overall || "-"}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{c.location} · {c.licensing_status}</p>
                <div className="mt-4 flex items-center justify-between">
                  <StarRating value={c.avg_overall} />
                  <span className="text-xs text-slate-400">{c.review_count} reviews</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Review wall: social proof */}
      {wall.length > 0 && (
        <section className="border-t border-[#DDDAD1] bg-ink-950/40">
          <div className="mx-auto max-w-[1800px] px-6 py-12 sm:px-10 sm:py-20 lg:px-16">
            <p className="eyebrow mb-3">What people are saying</p>
            <h2 className="text-3xl font-semibold">Real reviews from verified employees</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 sm:mt-10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {wall.map((r, i) => (
                <Link
                  key={r.id}
                  to={`/company/${r.company_id}`}
                  data-testid="wall-review"
                  className="card-surface lift flex h-full flex-col p-6 animate-fade-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="grid h-9 w-9 place-items-center rounded-full bg-teal-500/20 font-heading text-sm font-semibold text-teal-300">
                        {(r.job_title || "A").charAt(0).toUpperCase()}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-slate-200">{r.job_title || "Anonymous employee"}</p>
                        <p className="text-xs text-slate-500">{r.company_name}</p>
                      </div>
                    </div>
                    <span className="flex items-center gap-1 font-heading text-sm font-semibold text-teal-400">
                      {r.rating_overall?.toFixed(1)} <StarRating value={r.rating_overall} size={12} />
                    </span>
                  </div>
                  <Quote className="mt-4 h-4 w-4 text-teal-500/60" aria-hidden="true" />
                  <p className="mt-1 flex-1 text-sm leading-relaxed text-slate-300 line-clamp-3">{r.pros}</p>
                  <div className="mt-4 flex items-center justify-between">
                    {r.verified_employee ? <VerifiedBadge /> : <span />}
                    {r.red_flags?.length > 0 && (
                      <span className="flex items-center gap-1 text-xs text-coral-500"><Flag className="h-3 w-3" /> {r.red_flags.length}</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
